// Shared sign-in logic (used by Login, OTP, ChangePassword and DeviceGuard —
// never by a .desktop/.mobile file directly).
//
// Rules implemented here:
//  • One phone (APK → Evt_Users.MobileDeviceId) AND one browser (Web →
//    Evt_Users.DeviceName) per account. The binding is a SERVER-ISSUED token
//    (deviceIdentity.getDeviceToken) sent on every check; on the APK the API
//    also matches the hardware id, so a copied token is useless elsewhere.
//  • A bound account on a DIFFERENT device → OTP to move it here (the old device
//    is then rejected on its next check) — or a Super Admin resets the binding.
//  • Password still the default '1234' → compulsory /change-password before
//    anything else works (all platforms). Demo account is exempt from all of it.
import { VerifyUserDevice, BindUserDevice, getOrganisersByUser } from '../api/api';
import { isNativeApp, devicePlatform, getMobileDeviceId, getDeviceName, getDeviceModel, getDeviceToken, setDeviceToken } from './deviceIdentity';
import { isDemoMobile } from './demo';

export const DEFAULT_PASSWORD = '1234';
export const needsPasswordChange = (mobile, password) =>
  !isDemoMobile(mobile) && String(password ?? '') === DEFAULT_PASSWORD;

// Password rules shared by Register / ChangePassword.
export function passwordProblem(pw, mobile) {
  const p = String(pw || '');
  if (p.length < 4) return 'Password must be at least 4 characters';
  if (p.length > 20) return 'Password must be 20 characters or fewer';
  if (p === DEFAULT_PASSWORD) return `Password cannot be the default ${DEFAULT_PASSWORD}`;
  if (mobile && p === String(mobile)) return 'Password cannot be your mobile number';
  return '';
}

// 'APK' | 'MobileWeb' | 'Web' - the API keeps one device slot per value, and
// treats APK + MobileWeb as two halves of the same phone.
export const platform = () => devicePlatform();
const userAgent = () => (typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 200) : '');
// APK: hardware id (checked alongside the token). Web: the legacy per-browser id,
// only so rows bound by the old app upgrade to a token without an OTP.
export const thisDeviceId = async () => (isNativeApp() ? getMobileDeviceId() : getDeviceName());

// Ask the API whether these credentials may sign in on THIS device.
// bind=true → bind an unbound account to this device now (sign-in).
// Resolves { ok, reason, user?, boundModel?, boundDate? }; stores a new token if issued.
export async function verifyDevice(mobile, password, { bind = false } = {}) {
  const res = await VerifyUserDevice({
    MobileNumber: mobile, Password: password, DeviceId: await thisDeviceId(), DeviceToken: getDeviceToken(mobile),
    Platform: platform(), UserAgent: userAgent(), Bind: bind ? 1 : 0, DeviceModel: bind ? await getDeviceModel() : undefined,
  });
  if (res?.deviceToken) setDeviceToken(mobile, res.deviceToken);
  return res;
}

// After a successful OTP on a new device: move the binding here (API checks the
// OTP row, issues a fresh token → the old device is rejected from now on).
export async function bindThisDevice(user) {
  const res = await BindUserDevice({
    MobileNumber: user.MobileNumber, DeviceId: await thisDeviceId(), Platform: platform(), UserAgent: userAgent(), DeviceModel: await getDeviceModel(),
  });
  if (!res?.ok) throw new Error(res?.reason === 'otp_required' ? 'OTP not verified — request a new one' : 'Could not bind this device');
  setDeviceToken(user.MobileNumber, res.deviceToken);
  return res.user || user;
}

export const humanReason = (r) => ({
  not_found: 'Mobile number not registered',
  bad_password: 'Incorrect password',
  inactive: 'Your account is deactivated. Contact Admin.',
  device_mismatch: 'This account is signed in on another device.',
  unbound: 'Your device was reset by the admin. Please sign in again.',
}[r] || 'Sign-in failed');

// Load organisers for a verified user (login needs at least one).
export async function loadOrganisers(user) {
  const organisers = await getOrganisersByUser({ UserId: user.UserId });
  return Array.isArray(organisers) ? organisers : [];
}
